import { useState } from "react";
import { Copy, Check, Mail, MapPin, Send, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Reveal } from "./Reveal";
import { EMAIL } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const YEAR_LEVELS = ["Kindergarten", ...Array.from({ length: 12 }, (_, i) => `Year ${i + 1}`)];
const MODES = ["Online (Google Meet)", "In person", "Either / not sure"];

const inputCls =
    "w-full bg-transparent border border-bone/20 px-4 py-3.5 text-sm text-bone placeholder:text-bone/30 focus:border-bone/60 focus:outline-none transition-colors duration-300";

const Field = ({ label, children, testid }) => (
    <label className="block">
        <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-bone/50 mb-2">
            {label}
        </span>
        {children}
    </label>
);

export const Contact = () => {
    const [copied, setCopied] = useState(false);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        year_level: "",
        subjects: "",
        mode: "",
        message: "",
    });
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState("");

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(EMAIL);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            window.location.href = `mailto:${EMAIL}`;
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setStatus("sending");
        setError("");
        try {
            const res = await fetch(`${API}/enquiry`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...form, phone: form.phone || null, message: form.message || null }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data.detail || "Something went wrong");
            }
            setStatus("sent");
        } catch (err) {
            setError(typeof err.message === "string" ? err.message : "Something went wrong");
            setStatus("error");
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="py-28 md:py-40 bg-ink text-bone relative overflow-hidden">
            <div
                aria-hidden
                className="absolute -bottom-10 left-0 right-0 font-serif italic text-[22vw] leading-none text-outline opacity-30 select-none pointer-events-none whitespace-nowrap"
            >
                Enquire
            </div>
            <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
                <Reveal>
                    <div className="flex items-baseline gap-4 mb-4">
                        <span className="font-mono text-xs tracking-widest text-oxblood">Ch. 10</span>
                        <span className="font-mono text-xs uppercase tracking-[0.25em] text-bone/50">
                            Get in touch
                        </span>
                    </div>
                    <h2
                        data-testid="contact-heading"
                        className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-3xl"
                    >
                        Enquire today —<br />
                        <em className="text-bone/60">we reply fast.</em>
                    </h2>
                </Reveal>

                <div className="mt-14 grid lg:grid-cols-12 gap-14">
                    <Reveal delay={0.1} className="lg:col-span-7">
                        {status === "sent" ? (
                            <div
                                data-testid="enquiry-success-message"
                                className="border border-bone/20 p-10 md:p-14 text-center"
                            >
                                <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-6" strokeWidth={1.5} />
                                <h3 className="font-serif text-3xl mb-3">Enquiry sent.</h3>
                                <p className="text-bone/60 text-sm leading-relaxed max-w-md mx-auto">
                                    Thanks — your enquiry has landed in our inbox. Oliver or Ashton
                                    will get back to you shortly, usually within a day.
                                </p>
                                <button
                                    onClick={() => {
                                        setStatus("idle");
                                        setForm({ name: "", email: "", phone: "", year_level: "", subjects: "", mode: "", message: "" });
                                    }}
                                    data-testid="enquiry-send-another-button"
                                    className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-bone/70 border-b border-bone/30 pb-1 hover:text-bone transition-colors duration-300"
                                >
                                    Send another enquiry
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={submit} data-testid="enquiry-form" className="grid sm:grid-cols-2 gap-5">
                                <Field label="Your name *">
                                    <input
                                        required
                                        value={form.name}
                                        onChange={set("name")}
                                        data-testid="enquiry-name-input"
                                        className={inputCls}
                                        placeholder="Jane Citizen"
                                    />
                                </Field>
                                <Field label="Your email *">
                                    <input
                                        required
                                        type="email"
                                        value={form.email}
                                        onChange={set("email")}
                                        data-testid="enquiry-email-input"
                                        className={inputCls}
                                        placeholder="you@example.com"
                                    />
                                </Field>
                                <Field label="Phone (optional)">
                                    <input
                                        value={form.phone}
                                        onChange={set("phone")}
                                        data-testid="enquiry-phone-input"
                                        className={inputCls}
                                        placeholder="04xx xxx xxx"
                                    />
                                </Field>
                                <Field label="Student's year level *">
                                    <select
                                        required
                                        value={form.year_level}
                                        onChange={set("year_level")}
                                        data-testid="enquiry-year-level-select"
                                        className={`${inputCls} appearance-none ${form.year_level ? "" : "text-bone/30"}`}
                                    >
                                        <option value="" disabled className="text-ink">
                                            Select year level
                                        </option>
                                        {YEAR_LEVELS.map((y) => (
                                            <option key={y} value={y} className="text-ink">
                                                {y}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                                <Field label="Subject(s) needed *">
                                    <input
                                        required
                                        value={form.subjects}
                                        onChange={set("subjects")}
                                        data-testid="enquiry-subjects-input"
                                        className={inputCls}
                                        placeholder="e.g. Year 11 Chemistry"
                                    />
                                </Field>
                                <Field label="Online or in person? *">
                                    <select
                                        required
                                        value={form.mode}
                                        onChange={set("mode")}
                                        data-testid="enquiry-mode-select"
                                        className={`${inputCls} appearance-none ${form.mode ? "" : "text-bone/30"}`}
                                    >
                                        <option value="" disabled className="text-ink">
                                            Select preference
                                        </option>
                                        {MODES.map((m) => (
                                            <option key={m} value={m} className="text-ink">
                                                {m}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                                <div className="sm:col-span-2">
                                    <Field label="Anything else we should know? (goals, upcoming assessments, availability)">
                                        <textarea
                                            rows={4}
                                            value={form.message}
                                            onChange={set("message")}
                                            data-testid="enquiry-message-textarea"
                                            className={`${inputCls} resize-none`}
                                            placeholder="Tell us a little about the student and what you'd like help with…"
                                        />
                                    </Field>
                                </div>
                                {status === "error" && (
                                    <p
                                        data-testid="enquiry-error-message"
                                        className="sm:col-span-2 flex items-center gap-2 text-sm text-red-400"
                                    >
                                        <AlertCircle className="w-4 h-4 shrink-0" strokeWidth={1.75} />
                                        {error} — you can also email us directly at {EMAIL}.
                                    </p>
                                )}
                                <div className="sm:col-span-2">
                                    <button
                                        type="submit"
                                        disabled={status === "sending"}
                                        data-testid="enquiry-submit-button"
                                        className="group inline-flex items-center gap-3 bg-bone text-ink px-8 py-4 font-mono text-xs uppercase tracking-[0.2em] hover:bg-cream transition-colors duration-300 disabled:opacity-60"
                                    >
                                        {status === "sending" ? (
                                            <>
                                                Sending
                                                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.75} />
                                            </>
                                        ) : (
                                            <>
                                                Send enquiry
                                                <Send className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={1.75} />
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </Reveal>

                    <Reveal delay={0.18} className="lg:col-span-5">
                        <div className="border border-bone/20 p-8">
                            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-bone/50 mb-4">
                                Prefer email?
                            </p>
                            <a
                                href={`mailto:${EMAIL}?subject=Tutoring%20enquiry%20—%20North%20Shore%20Scholars`}
                                data-testid="contact-email-link"
                                className="flex items-center gap-3 text-bone hover:text-cream transition-colors duration-300 break-all"
                            >
                                <Mail className="w-4 h-4 text-oxblood shrink-0" strokeWidth={1.75} />
                                <span className="font-mono text-sm">{EMAIL}</span>
                            </a>
                            <button
                                onClick={copy}
                                data-testid="contact-copy-email-button"
                                className="mt-5 inline-flex items-center gap-2 border border-bone/25 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-bone/80 hover:border-bone hover:text-bone transition-colors duration-300"
                            >
                                {copied ? (
                                    <Check className="w-3.5 h-3.5 text-green-400" strokeWidth={1.75} />
                                ) : (
                                    <Copy className="w-3.5 h-3.5" strokeWidth={1.75} />
                                )}
                                {copied ? "Copied" : "Copy email"}
                            </button>
                            <p className="mt-8 text-bone/50 text-sm leading-relaxed">
                                Tell us the student's year level, subjects and goals, and whether
                                you'd prefer online or in-person sessions. We'll match you with
                                Oliver or Ashton and find a time that works.
                            </p>
                            <div className="mt-8 pt-6 border-t border-bone/10 flex items-center gap-3 text-bone/50">
                                <MapPin className="w-4 h-4 shrink-0" strokeWidth={1.75} />
                                <span className="font-mono text-[10px] uppercase tracking-[0.25em]">
                                    Sydney's North Shore · Online everywhere
                                </span>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
